<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useUserStore } from '@/stores'
import {
  PaymentMethodSelector,
  StripeCheckoutPopup,
  createShowPaymentMethodSelector,
  useFixedBottomPayment,
  usePurchase,
} from '@/composables/payment'

defineProps<{
  config: any
}>()

const user = useUserStore()

const {
  templateProducts,
  PayTemplate,
  currentPaymentMethod,
  availablePaymentMethods,
  selectPaymentMethod,
  stripeClientSecret,
  stripeVisible,
  setStripeVisible,
  onStripeComplete,
  onStripeCancel,
} = usePurchase('store')

const showPaymentMethodSelector = createShowPaymentMethodSelector(availablePaymentMethods)
const { isFixedBottom, containerRef: payCoverRef, checkContentHeight } = useFixedBottomPayment()

watch(currentPaymentMethod, () => {
  checkContentHeight()
})

function bonusPercent(item: any): number {
  return Math.round((item.coinsBonus / item.coinsBuy) * 100)
}

// C 位档位：sequence > 100 渲染为横向大卡片，其余走普通网格
const cPositionProducts = computed(() =>
  (templateProducts.value || []).filter((i: any) => Number(i.sequence) > 100),
)
const normalProducts = computed(() =>
  (templateProducts.value || []).filter((i: any) => !(Number(i.sequence) > 100)),
)

// 选中档位：列表变化（含切换支付方式 tab）时默认选中第一个 C 位档位（无 C 位则选第一个）
const selectedProductId = ref<string | number | null>(null)
watch(templateProducts, (products: any[]) => {
  const list = products || []
  if (!list.length) {
    selectedProductId.value = null
    return
  }
  const cFirst = list.find((i: any) => Number(i.sequence) > 100)
  selectedProductId.value = (cFirst || list[0]).productId
}, { immediate: true })

function onSelectProduct(item: any) {
  selectedProductId.value = item.productId
  PayTemplate(item)
}
</script>

<template>
  <div class="pay-page min-h-screen bg-[#100A1E]">
    <!-- 导航栏 -->
    <BasicNavbar class="text-white">
      <span class="absolute left-1/2 -translate-x-1/2 text-20px font-bold" style="color: #FFFFFF;">Store</span>
    </BasicNavbar>

    <div ref="payCoverRef" class="pt-44px">
      <!-- 余额行 -->
      <div class="pt-22px px-16px">
        <span class="text-14px text-white font-400" style="font-family: Inter-Regular, sans-serif;">Balance: </span>
        <span class="text-18px font-500" style="color: rgba(187, 122, 251, 1); font-family: Inter-Medium, sans-serif;">{{ user.$state.coinBalance }}</span>
        <span class="text-14px text-white font-400" style="font-family: Inter-Regular, sans-serif;"> Diamonds</span>
      </div>

      <!-- C 位档位（sequence > 100）：横向大卡片 -->
      <div
        v-for="item in cPositionProducts"
        :key="`c-${item.productId}`"
        class="c-card relative mx-14px mt-12px cursor-pointer overflow-hidden"
        :class="{ selected: item.productId === selectedProductId }"
        @click="onSelectProduct(item)"
      >
        <!-- 营销标签 -->
        <div v-if="item.marketingText" class="c-popular-badge">
          {{ item.marketingText }}
        </div>
        <!-- 百分比角标（右上角） -->
        <div v-if="item.coinsBuy > 0 && item.coinsBonus > 0" class="percent-badge absolute right-0 top-0">
          +{{ bonusPercent(item) }}%
        </div>
        <div class="c-card-body flex items-center justify-between">
          <div class="flex items-center">
            <img src="/images/vowpath/coins/coin-diamond.webp" class="w-26px h-23px" alt="">
            <span class="c-coins ml-4px">{{ item.coinsBuy }}</span>
            <span v-if="item.coinsBonus > 0" class="c-bonus">+{{ item.coinsBonus }}</span>
          </div>
          <div class="c-price">
            $ {{ item.price }}
          </div>
        </div>
      </div>

      <!-- 套餐卡片网格 -->
      <div class="pay-grid mx-14px" :class="cPositionProducts.length ? 'mt-12px' : 'mt-20px'">
        <div
          v-for="item in normalProducts"
          :key="item.productId"
          class="card relative w-full rounded-8px cursor-pointer overflow-hidden"
          :class="{ selected: item.productId === selectedProductId }"
          @click="onSelectProduct(item)"
        >
          <!-- Popular 角标（左上角） -->
          <div
            v-if="item.marketingText"
            class="popular-badge absolute left-0 top-0 flex items-center"
          >
            <span class="popular-text">{{ item.marketingText }}</span>
          </div>

          <!-- 百分比角标（右上角） -->
          <div
            v-if="item.coinsBuy > 0 && item.coinsBonus > 0"
            class="percent-badge absolute right-0 top-0"
          >
            +{{ bonusPercent(item) }}%
          </div>

          <!-- 钻石图标 + 数量 + 加赠（同行，居中） -->
          <div class="coins-row flex items-center justify-center">
            <img
              src="/images/vowpath/coins/coin-diamond.webp"
              class="w-21px h-18px"
              alt=""
            >
            <span class="coins-text ml-2px">{{ item.coinsBuy }}</span>
            <span v-if="item.coinsBonus > 0" class="coins-bonus">+</span>
            <span v-if="item.coinsBonus > 0" class="coins-bonus">{{ item.coinsBonus }}</span>
          </div>

          <!-- 价格条 -->
          <div class="price-row absolute bottom-0 left-0 right-0 flex justify-center">
            <span class="text-16px font-500" style="color: rgba(255, 212, 131, 1); font-family: Inter-Medium, sans-serif; line-height: 12px;">$ {{ item.price }}</span>
          </div>
        </div>
      </div>

      <!-- 支付方式选择器 -->
      <div
        v-if="showPaymentMethodSelector"
        class="flex justify-center"
        :class="[
          isFixedBottom ? 'fixed bottom-0px left-0 right-0 z-50' : 'px-10px mb-12px mt-12px',
        ]"
      >
        <PaymentMethodSelector
          variant="large"
          :dark="true"
          :model-value="currentPaymentMethod"
          :available-methods="availablePaymentMethods"
          @update:model-value="selectPaymentMethod"
        />
      </div>

      <!-- Tips 文案区 -->
      <div :class="`${isFixedBottom ? 'pb-60px' : 'pb-20px'} px-16px mt-10px`">
        <p class="text-11px leading-17px" style="color: rgba(165, 165, 165, 1); letter-spacing: 0.22px; font-family: HelveticaNeue, sans-serif;">
          {{ config.texts.tips }}
        </p>
        <p class="text-11px leading-17px mt-6px" style="color: rgba(165, 165, 165, 1); letter-spacing: 0.22px;">
          {{ config.texts.tip1 }}
        </p>
        <p class="text-11px leading-17px mt-6px" style="color: rgba(165, 165, 165, 1); letter-spacing: 0.22px;">
          {{ config.texts.tip2 }}
        </p>
        <p class="text-11px leading-17px mt-6px" style="color: rgba(165, 165, 165, 1); letter-spacing: 0.22px;">
          {{ config.texts.tip3 }}
        </p>
      </div>
    </div>

    <!-- Stripe 内嵌支付弹窗 -->
    <StripeCheckoutPopup
      :value="stripeVisible"
      :client-secret="stripeClientSecret"
      @input="setStripeVisible"
      @cancel="onStripeCancel"
      @complete="onStripeComplete"
    />
  </div>
</template>

<style lang="scss" scoped>
/* C 位档位横向大卡片（设计稿规格：343x80 渐变底 + 2px 边框） */
.c-card {
  height: 80px;
  background: linear-gradient(90deg, rgba(163, 52, 162, 1) 0%, rgba(84, 34, 136, 1) 100%) padding-box,
              linear-gradient(135deg, #B272B1, #672EA2) border-box;
  border: 2px solid transparent;
  border-radius: 8px;
  box-sizing: border-box;
}

/* 选中态：亮彩渐变边框（蓝湖选中态描边） */
.c-card.selected {
  background: linear-gradient(90deg, rgba(163, 52, 162, 1) 0%, rgba(84, 34, 136, 1) 100%) padding-box,
              linear-gradient(135deg, #AAEDFF 0%, #FF7CCC 50%, #FFC69B 100%) border-box;
}

.c-card-body {
  height: 100%;
  padding: 0 13px 0 11px;
}

.c-popular-badge {
  position: absolute;
  left: 0;
  top: 0;
  background: linear-gradient(135deg, rgba(229, 197, 130, 1) 0%, rgba(224, 163, 73, 1) 100%);
  border-radius: 6px 0 10px 0;
  padding: 3px 7px;
  font-size: 10px;
  font-weight: 500;
  line-height: 12px;
  color: rgba(106, 53, 13, 1);
  font-family: Inter-Medium, sans-serif;
}

.c-coins {
  font-size: 18px;
  font-weight: 700;
  line-height: 14px;
  color: #fff;
  font-family: Inter-Bold, sans-serif;
}

.c-bonus {
  margin-left: 4px;
  font-size: 18px;
  font-weight: 700;
  line-height: 14px;
  color: rgba(255, 189, 88, 1);
  font-family: Inter-Bold, sans-serif;
}

.c-price {
  font-size: 18px;
  font-weight: 700;
  line-height: 18px;
  font-family: Inter-Bold, sans-serif;
  background: linear-gradient(90deg, rgba(156, 94, 249, 1) 0%, rgba(248, 107, 205, 1) 33.052454%, rgba(253, 181, 71, 1) 64.925636%, rgba(255, 226, 164, 1) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* 套餐卡片网格 */
.pay-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.card {
  height: 114px;
  background: linear-gradient(135deg, rgba(44, 39, 61, 1) 0%, rgba(28, 16, 45, 1) 100%) padding-box,
              linear-gradient(135deg, rgba(73, 67, 93, 1), rgba(57, 45, 74, 1)) border-box;
  border-radius: 8px;
  box-sizing: border-box;
  border: 2px solid transparent;
}

/* 选中态：亮彩渐变边框（蓝湖选中态描边） */
.card.selected {
  background: linear-gradient(135deg, rgba(44, 39, 61, 1) 0%, rgba(28, 16, 45, 1) 100%) padding-box,
              linear-gradient(135deg, #AAEDFF 0%, #FF7CCC 50%, #FFC69B 100%) border-box;
}

.popular-badge {
  background: linear-gradient(135deg, rgba(71, 45, 119, 1) 0%, rgba(100, 56, 103, 1) 100%);
  border-radius: 0px 0px 10px 0px;
  padding: 3px 7px;
}

.popular-text {
  background: linear-gradient(90deg, rgba(204, 66, 247, 1) 0%, rgba(239, 106, 208, 1) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-size: 10px;
  font-family: Inter-Medium, sans-serif;
  font-weight: 500;
  white-space: nowrap;
  line-height: 12px;
}

/* 百分比角标（右上角，粉色实底） */
.percent-badge {
  background: rgba(243, 70, 160, 1);
  border-radius: 10px 0 0 10px;
  padding: 2px 4px;
  font-size: 10px;
  font-weight: 500;
  line-height: 14px;
  color: #fff;
  font-family: Inter-Medium, sans-serif;
  white-space: nowrap;
}

/* 钻石数量行（设计稿 margin: 角标行 18px + 15px，水平居中） */
.coins-row {
  margin: 35px 12px 0;
}

.coins-text {
  font-size: 17px;
  font-weight: 700;
  line-height: 14px;
  color: #fff;
  font-family: Inter-Bold, sans-serif;
}

.coins-bonus {
  margin-left: 4px;
  font-size: 17px;
  font-weight: 700;
  line-height: 14px;
  color: rgba(200, 107, 248, 1);
  font-family: Inter-Bold, sans-serif;
}

.price-row {
  background: rgba(254, 229, 205, 0.06);
  padding: 11px 0 13px 0;
}
</style>
